// EndOfHandReport.tsx - the locked DX6 end-of-hand report (Chess.com end-of-game style). Purely
// presentational: it renders a ReportModel and holds no grade logic, so Solo (S24) reuses it verbatim by
// passing its own model (built from a generated hand's grades + Solo-flavored header/verdict copy).
//
// Structure transcribed 1:1 from the mockup (local-mode-read-first-v2.html lines 543-562, renderReport):
//   - a hand-accuracy verdict card (big % colored by move class + one-line verdict + sub)
//   - "Accuracy across the hand": the SVG line, dots colored by move class
//   - "Grade bands": non-zero band counts in the exact Chess.com colors
//   - "Street by street": one row per street with a per-street rating chip
import { MOVE_CLASS_LABELS } from '../grade/types';
import type { ReportModel } from './report';
import { AccuracyChart } from './AccuracyChart';

/** A section header (gold bar + title + sub), matching the .sec/.stt-row primitive used across surfaces. */
function SectionHead({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="stt-row">
      <span className="bar" />
      <div>
        <div className="stt">{title}</div>
        <div className="sd">{sub}</div>
      </div>
    </div>
  );
}

export function EndOfHandReport({ model }: { model: ReportModel }) {
  return (
    <div className="report">
      <div>
        <div className="kicker">{model.kicker}</div>
        <div className="h2">{model.h2}</div>
        <div className="lede">{model.lede}</div>
      </div>

      {/* verdict card - the big accuracy number colored by move class */}
      <div className="rephead">
        <div className="acc" style={{ color: `var(--${model.badgeCls})` }}>
          {model.accuracy}%
        </div>
        <div className="accsub">hand accuracy</div>
        <div className="vd">{model.verdict}</div>
        <div className="vdsub">{model.sub}</div>
      </div>

      <div className="sec">
        <SectionHead title="Accuracy across the hand" sub="Each decision, best to blunder. Watch where it dipped." />
        <div className="chart">
          <AccuracyChart streets={model.streets} />
        </div>
      </div>

      <div className="sec">
        {/* FULL: counted, not assumed. A hand is not four decisions - hero can be re-raised preflop and act
            twice before a flop (five), or fold the first one (one). */}
        <SectionHead
          title="Grade bands"
          sub={`How your ${model.streets.length} ${model.streets.length === 1 ? 'decision' : 'decisions'} landed.`}
        />
        <div className="bands">
          {model.bands.map(([cls, label, count]) => (
            <div className="band" key={cls}>
              <span className="dot" style={{ background: `var(--${cls})` }} />
              {label} <span className="c">x{count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="sec">
        <SectionHead title="Street by street" sub="The action you took and how it scored." />
        <div className="brows">
          {model.streets.map((s) => (
            <div className="brow" key={s.name}>
              <div className="bn">{s.name}</div>
              <div className="ba">{s.action}</div>
              <div
                className="rc"
                style={{ color: `var(--${s.cls})`, border: `1px solid var(--${s.cls})` }}
              >
                {MOVE_CLASS_LABELS[s.cls]}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
